import { useState } from 'react';
import { ChevronsDownUp, ChevronsUpDown, RefreshCw } from 'lucide-react';
import type { SignalRow } from '@shared/flow/signal-view';
import { useRefreshSignals } from './useRefreshSignals';
import { RankingLogicModal } from './RankingLogicModal';
import { unverifiedDotTitle, unverifiedRunningCount } from './unverifiedDot';

/**
 * Signals tab toolbar-trailing content (bead .17, U7): the Refresh button
 * (spinner while a refresh is in flight), an amber dot when any loaded
 * live row's Gong probe is unverified, and the "?" -> "How Ranking Works"
 * trigger. Mounted via `FlowTable`'s existing `toolbarTrailing` slot from
 * `SignalsFlowTable.tsx` — no change to `FlowTable` itself.
 *
 * QA batch 1 Q4 / D-B: an Expand all / Collapse all toggle, left of Refresh,
 * driven by `SignalsFlowTable`'s `groupCollapse` prop. `null`/omitted on an
 * ungrouped view (nothing to expand/collapse) hides the button entirely.
 */

/** Shared by Refresh and the Expand/Collapse-all toggle — the `disabled:`
 *  variants are inert on the toggle (it is never disabled) but keeping one
 *  constant means the two buttons can never silently drift apart. */
const TOOLBAR_BUTTON_CLASS =
  'flex items-center gap-1 rounded-md border border-secondary dark:border-nightshade-700 px-2 py-1 text-[11px] font-semibold text-content-secondary dark:text-nightshade-300 hover:bg-bg-secondary dark:hover:bg-nightshade-800 disabled:opacity-60 disabled:cursor-wait';

export function SignalsToolbar({
  rows,
  groupCollapse,
  now,
}: {
  rows: SignalRow[];
  /** The table's shared minute-granular clock (`SignalsFlowTable`'s `now` memo) — the same instant the rows' buttons resolve against. */
  now: Date;
  /** `null`/omitted on an ungrouped view — no toggle renders. */
  groupCollapse?: { allCollapsed: boolean; onToggle: () => void } | null;
}) {
  const { isPending, lastResult, error, refresh } = useRefreshSignals();
  const [showRankingLogic, setShowRankingLogic] = useState(false);

  const unverifiedCount = unverifiedRunningCount(rows, now);

  // Plan "Deferred" lean: throttled and network-failure states explain
  // themselves via this quiet `title` tooltip, never a toast — see the
  // 202 fail-soft scenario below, which by contrast gets NO explanation at
  // all (Salesforce being unreachable server-side is not a rep-facing
  // error; D9's deploy note).
  const title = lastResult?.throttled
    ? 'Refreshed less than 5 minutes ago'
    : error
      ? error.message
      : isPending
        ? 'Refreshing…'
        : 'Refresh';

  const collapseLabel = groupCollapse?.allCollapsed ? 'Expand all' : 'Collapse all';

  return (
    <div className="flex items-center gap-2">
      {showRankingLogic && <RankingLogicModal onClose={() => setShowRankingLogic(false)} />}
      {unverifiedCount > 0 && (
        <span
          className="w-2 h-2 rounded-full bg-content-warning flex-shrink-0"
          title={unverifiedDotTitle(unverifiedCount)}
        />
      )}
      {groupCollapse && (
        <button
          type="button"
          aria-label={collapseLabel}
          title={collapseLabel}
          onClick={groupCollapse.onToggle}
          className={TOOLBAR_BUTTON_CLASS}
        >
          {groupCollapse.allCollapsed ? <ChevronsUpDown className="w-3 h-3" /> : <ChevronsDownUp className="w-3 h-3" />}
          {collapseLabel}
        </button>
      )}
      <button
        type="button"
        aria-label="Refresh signals"
        title={title}
        disabled={isPending}
        // B4: the loaded rows ride along so the server re-verifies them
        // against Gong too (All Signals' other-rep rows included).
        onClick={() => refresh({ signalIds: rows.map((row) => row.signal_id) })}
        className={TOOLBAR_BUTTON_CLASS}
      >
        <RefreshCw className={`w-3 h-3 ${isPending ? 'animate-spin' : ''}`} />
        Refresh
      </button>
      <button
        type="button"
        aria-label="How Ranking Works"
        title="How ranking works"
        onClick={() => setShowRankingLogic(true)}
        className="w-5 h-5 rounded-full border border-secondary dark:border-nightshade-600 text-content-tertiary dark:text-content-placeholder hover:text-content-secondary dark:hover:text-nightshade-300 hover:border-content-tertiary dark:hover:border-nightshade-500 text-[11px] font-semibold flex items-center justify-center flex-shrink-0"
      >
        ?
      </button>
    </div>
  );
}
